// Search helpers for line/route pickers, whose options are named as legs:
// "Sarandë - Prizren (verore)".
//
// Two problems they solve:
//  1. Accents. A standard keyboard can't type ë/ç/đ, so a term has to compare
//     equal to the accented text the API returns.
//  2. Direction. Users express it with the separator — "sarande -" means
//     departure, "- sarande" means arrival — but the API can't be relied on for
//     either: it drops a trailing separator ("prizren -" returns the same rows
//     as "prizren"), and its leading-separator handling is a literal substring
//     match, so any line named "Prizren -Sarandë" or "Prizren- Sarandë" is
//     missed. Both directions are therefore resolved here, client-side, against
//     a normalized separator.

// đ, ø and ł are single codepoints with no canonical decomposition, so NFD
// leaves them alone and they need spelling out.
const UNDECOMPOSABLE: Record<string, string> = {
  đ: "d",
  Đ: "D",
  ø: "o",
  Ø: "O",
  ł: "l",
  Ł: "L"
};

// Every dash users or data might use, plus any whitespace around it.
const SEPARATOR = /\s*[-–—]\s*/g;

// Folds to what a standard keyboard can type: NFD splits a precomposed letter
// into base + combining mark, and the range strips the mark. Mirrors the
// backend's Latin1_General_CI_AI collation.
export const foldSearchText = (value: string): string =>
  value
    .replace(/[đĐøØłŁ]/g, (ch) => UNDECOMPOSABLE[ch] || ch)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .trim();

// Folds AND pins the separator to exactly " - ", so "Sarandë-Prizren",
// "Sarandë -Prizren" and "Sarandë  -  Prizren" all compare identically — which
// is what lets a typed "sarande -" reach inconsistently named lines.
const normalizeLeg = (value: string): string =>
  foldSearchText(value)
    .replace(SEPARATOR, " - ")
    .replace(/\s+/g, " ")
    .trim();

export type LegSearchScope = {
  // "filter" drops non-matches outright — the user named a direction, so the
  // opposite one is noise. "rank" keeps everything, matches first.
  mode: "filter" | "rank";
  matches: (label: string) => boolean;
};

export const buildLegSearchScope = (term: string): LegSearchScope => {
  const normalized = normalizeLeg(term);
  // Text, then a dash: a departure ("sarande -", "sarande - tirane").
  const isDeparture = /^[^-].*-/.test(normalized);
  // A dash, then text: an arrival ("- sarande"). A lone dash is neither, and
  // falls through to ranking rather than filtering the list down to nothing.
  const isArrival = /^-\s*\S/.test(normalized);
  if (isArrival)
    return {
      mode: "filter",
      // `includes`, not `endsWith`: labels carry trailing notes like "(verore)".
      matches: (label) => normalizeLeg(label).includes(normalized)
    };
  return {
    mode: isDeparture ? "filter" : "rank",
    matches: (label) => normalizeLeg(label).startsWith(normalized)
  };
};

// What to actually send to the API: the first city named, with its original
// accents and casing intact (the backend is accent-insensitive, and folding
// here would only risk mangling a term it handles fine). The separator is
// stripped because the API can't filter on it reliably — direction is applied
// by `buildLegSearchScope` once the rows come back. "- sarande" and "sarande -"
// therefore both fetch the same candidate set.
export const legSearchApiTerm = (term: string): string =>
  term.split(SEPARATOR).map((part) => part.trim()).filter(Boolean)[0] || "";
